// src/context/CartContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { showSuccessToast, showErrorToast } from '../utils/toastConfig';
// Asume que tienes un 'types/index.ts' con el tipo 'CartItem'
import { CartItem } from '../types';
import axios from 'axios';

interface CartContextType {
  isCartOpen: boolean;
  cartItems: CartItem[];
  loading: boolean;
  cartTotal: number;
  openCart: () => void;
  closeCart: () => void;
  addItemToCart: (productId: string, quantity: number, stockLocation?: string, variantId?: number) => Promise<void>;
  removeItemFromCart: (itemId: number) => Promise<void>;
  updateItemQuantity: (itemId: number, quantity: number) => Promise<void>;
  refreshCart: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart debe ser usado dentro de un CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }: { children: ReactNode }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);

  // --- FUNCIÓN CORREGIDA PARA GUARDAR LA LLAVE ---
  const handleSessionKey = (response: any) => {
    try {
      const keyFromBody = response?.data?.session_key;
      let keyFromHeader: string | undefined;
      const headers = response?.headers || {};
      
      // Normalizar nombres de headers y buscar 'x-session-key'
      for (const [k, v] of Object.entries(headers)) {
        if (k && k.toLowerCase() === 'x-session-key') {
          keyFromHeader = String(v);
          break;
        }
      }
      
      const sessionKey = keyFromBody || keyFromHeader;

      // FILTRO DE SEGURIDAD: Si la llave recibida es basura, la ignoramos.
      const BAD_KEYS = ['null', 'undefined', 'None', '', 'NaN'];
      if (sessionKey && BAD_KEYS.includes(String(sessionKey))) {
        console.warn('--- DEBUG: El backend devolvió una llave inválida, se ignora ---', sessionKey);
        return; 
      }

      const existingKey = localStorage.getItem('cart_session_key');
  
      if (sessionKey && (!existingKey || existingKey !== sessionKey)) {
        console.log('--- DEBUG: Guardando nueva session_key válida ---', { sessionKey });
        localStorage.setItem('cart_session_key', sessionKey);
      } 
    } catch (e) {
      console.error('--- DEBUG: error procesando session_key ---', e);
    }
  };
  // ------------------------------------------------

  // Función para cargar el carrito desde el backend
  const fetchCart = async () => {
    setLoading(true);
    try {
      // console.log('--- DEBUG: fetchCart iniciado ---');
      const response = await api.get('orders/cart/current/');
      setCartItems(response.data.items || []);
      handleSessionKey(response);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        console.error('--- ERROR BACKEND (fetchCart) ---', error.response.data);
      } else {
        console.error('Error al cargar el carrito:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Cargar el carrito automáticamente al iniciar
  useEffect(() => {
    fetchCart();
  }, []);

  const openCart = () => {
    fetchCart();
    setIsCartOpen(true);
  };

  const closeCart = () => setIsCartOpen(false);

  // AÑADIR ITEM
  const addItemToCart = async (productId: string, quantity: number, stockLocation?: string, variantId?: number) => {
    setLoading(true);
    try {
      const payload: any = {
        product_id: productId,
        quantity: quantity
      };
      
      // Agregar ubicación si se especificó
      if (stockLocation) {
        payload.stock_location = stockLocation;
      }

      // Agregar variante si se especificó
      if (variantId) {
        payload.variant = variantId;
      }
      
      const response = await api.post('/orders/cart/add_item/', payload);
      handleSessionKey(response);
      setCartItems(response.data.items || []);
      showSuccessToast(toast, 'Producto agregado al carrito');
    } catch (error) {
      showErrorToast(toast, 'Error al agregar el producto');
      if (axios.isAxiosError(error) && error.response) {
        console.error('--- ERROR BACKEND (addItemToCart) ---', error.response.data);
        // Mostrar mensaje del backend si está disponible
        const errorMsg = error.response.data?.detail || 'Error al agregar el producto';
        showErrorToast(toast, errorMsg);
      } else {
        console.error('Error al añadir item:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ELIMINAR ITEM
  const removeItemFromCart = async (itemId: number) => {
    setLoading(true);
    try {
      const response = await api.delete(`/orders/cart/remove-item/${itemId}/`);
      handleSessionKey(response);
      setCartItems(response.data.items || []);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        console.error('--- ERROR BACKEND (removeItemFromCart) ---', error.response.data);
      } else {
        console.error('Error al eliminar item:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // ACTUALIZAR CANTIDAD
  const updateItemQuantity = async (itemId: number, quantity: number) => {
    setLoading(true);
    try {
      const response = await api.patch(`/orders/cart/update-item/${itemId}/`, {
        quantity: quantity
      });
      handleSessionKey(response);
      // Asegurar que el estado refleja el backend (algunas serializaciones pueden tardar)
      setCartItems(response.data.items || []);
      // Refrescar carrito para evitar estados desfasados
      await fetchCart();
      showSuccessToast(toast, 'Cantidad actualizada');
    } catch (error) {
      showErrorToast(toast, 'No se pudo actualizar la cantidad');
      if (axios.isAxiosError(error) && error.response) {
        console.error('--- ERROR BACKEND (updateItemQuantity) ---', error.response.data);
        const errorMsg = (error.response.data as any)?.detail || 'Error al actualizar cantidad';
        showErrorToast(toast, errorMsg);
      } else {
        console.error('Error al actualizar cantidad:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  // Calcular total
  const cartTotal = cartItems.reduce((acc, item) => {
    const itemPrice = Number((item as any).unit_price) || Number((item as any).price) || 0;
    const itemQuantity = Number(item.quantity) || 0;
    return acc + (itemPrice * itemQuantity);
  }, 0);

  const value = {
    isCartOpen,
    cartItems,
    loading,
    cartTotal,
    openCart,
    closeCart,
    addItemToCart,
    removeItemFromCart,
    updateItemQuantity,
    refreshCart: fetchCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};